import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import {
    ArrowLeft,
    User,
    Plus,
    AlertTriangle,
    Clock,
    CalendarPlus,
    ReceiptIndianRupee,
} from "lucide-react";
import AddAppointmentModal from "@/components/Appointment/AddAppointmentModal";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import { usePatientData } from "@/hooks/usePatientData";
import { usePrescriptionActions } from "@/hooks/usePrescriptionActions";

import PrescriptionForm, { type RxFormActions } from "@/components/Prescription/PrescriptionForm";
import PatientOverviewTab from "@/components/Patient/PatientOverviewTab";
import ContextPanel from "@/components/Prescription/ContextPanel";
import ViewPrescriptionModal from "@/components/Prescription/PrintPrescriptionPreviewModal";
import VisitTimeline from "@/components/Patient/VisitTimeline";
import BillingTab from "@/components/Patient/BillingTab";
import PatientFormModal from "@/components/Patient/PatientFormModal";
import PatientIPDTab from "@/components/Patient/PatientIPDTab";
import PatientDetailsPanel from "@/components/Patient/PatientDetailsPanel";
import PatientDocuments from "@/components/PatientDocuments/PatientDocuments";
import PatientVitalsTab from "@/components/Vitals/PatientVitalsTab";
import { patientAgeMonths } from "@/lib/vitals";
import VitalsStrip from "@/components/Vitals/VitalsStrip";
import { PatientBanner } from "@/components/Patient/PatientBanner";
import ConsultationControls from "@/components/Queue/ConsultationControls";
import { TodayVitalsProvider } from "@/context/TodayVitalsContext";
import FormActionBar from "@/components/PrescriptionForm/StickyBar";

import { getSettings, getPatientDeposits } from "@/lib/api";
import { format } from "date-fns";
import { Doctor } from "@/types/doctor";
import { usePageTitle } from "@/hooks/usePageTitle";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PatientDetailsPageProps {
    onNavigateBack?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Keyed by patient so "Finish & call next" (which navigates to the next patient's page) starts a fresh
 * page and prescription, and one source of today's vitals feeds the header strip and the vitals section.
 */
export default function PatientDetailsPage(props: PatientDetailsPageProps) {
    const { id } = useParams<{ id: string }>();
    return (
        <TodayVitalsProvider key={id} patientId={id || ""}>
            <PatientDetailsPageContent {...props} />
        </TodayVitalsProvider>
    );
}

function PatientDetailsPageContent({ onNavigateBack }: PatientDetailsPageProps) {
    const { currentUser } = useAuth();
    const { can, hasFeature } = usePermission();
    const navigate = useNavigate();
    const { id: patientId } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    // ?tab=billing (from "Patient's bills" on the Billing page) opens that tab
    const [activeTab, setActiveTab] = useState(() => {
        const tab = searchParams.get("tab");
        if (tab === "billing" && !can('billing:read')) return "overview";
        return tab && ["overview", "prescriptions", "vitals", "billing", "ipd", "documents"].includes(tab) ? tab : "overview";
    });
    const [showEditModal, setShowEditModal] = useState(false);
    // Banner actions: Book opens the booking dialog here; New bill opens the Billing tab's own New bill dialog
    const [booking, setBooking] = useState(false);
    // Patient details (contact, address, emergency contact, background) — a side panel from the banner
    const [showDetails, setShowDetails] = useState(false);
    const [newBill, setNewBill] = useState(false);
    const clearNewBill = useCallback(() => setNewBill(false), []);

    // Doctor profile — fetched once, passed into PrescriptionForm
    const [doctor, setDoctor] = useState<Doctor | null>(null);

    // Use safe ID so hooks are always called unconditionally
    const safePatientId = patientId || "";

    const {
        patient, visits, visitRecords, appointments, loading,
        fetchPatientData,
    } = usePatientData(safePatientId);
    // Visits, counting a visit once however many prescriptions it has; older data falls back to prescriptions
    const visitCount = visitRecords.length || visits.length;
    // "3 visits since Jun 2026" — the earliest visit or prescription on record
    const firstVisit = [...visitRecords.map(v => v.date), ...visits.map(v => v.date)]
        .map(d => new Date(d)).filter(d => !isNaN(d.getTime()))
        .reduce<Date | null>((min, d) => (!min || d < min ? d : min), null);
    // Most recent visit, for Patient details — the record's own date covers visits without a prescription
    const lastVisit = [...visitRecords.map(v => v.date), ...visits.map(v => v.date), patient?.last_visit_date ?? ""]
        .filter(d => d && !isNaN(new Date(d).getTime()))
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;

    // The follow-up the last prescription asked for, if it's still ahead: booking starts on that day
    const followUpDate = visits.map(v => String(v.prescription?.next_visit?.date ?? "").slice(0, 10))
        .filter(d => d >= format(new Date(), "yyyy-MM-dd")).sort()[0];

    // Amount due — the patient's ledger balance, the same figure the Billing tab shows; refreshed when the tab changes
    const [outstanding, setOutstanding] = useState(0);
  usePageTitle(patient?.name);

    const prescriptionActions = usePrescriptionActions(safePatientId, fetchPatientData);

    const patientHeaderRef = useRef<HTMLDivElement>(null);

    // Lifted form state — drives the FormActionBar rendered at page level
    const rxSubmitRef = useRef<(() => void) | null>(null);
    const rxActionsRef = useRef<RxFormActions | null>(null);
    const [rxFormState, setRxFormState] = useState<{
        hasChanges: boolean; isSubmitting: boolean; hasValidChanges: boolean; attachmentsSlot: ReactNode; pendingAttachmentsCount: number; summary: string;
    }>({ hasChanges: false, isSubmitting: false, hasValidChanges: false, attachmentsSlot: null, pendingAttachmentsCount: 0, summary: "" });

    // Next / End consultation with an unsaved prescription: save first, then run it from onSaved.
    // Cleared by the pad's own Save, Save & print and Cancel, so a failed save never calls someone later.
    const afterSaveRef = useRef<(() => void) | null>(null);
    const [queueMain, setQueueMain] = useState(false);
    const saveThen = useCallback((then: () => void) => { afterSaveRef.current = then; rxSubmitRef.current?.(); }, []);

    const handleFormReady = useCallback(
        (submitFn: () => void, hasChanges: boolean, isSubmitting: boolean, hasValidChanges: boolean, attachmentsSlot: ReactNode, pendingAttachmentsCount: number, summary: string, actions: RxFormActions) => {
            rxSubmitRef.current = submitFn;
            rxActionsRef.current = actions;
            setRxFormState({ hasChanges, isSubmitting, hasValidChanges, attachmentsSlot, pendingAttachmentsCount, summary });
        },
        []
    );

    // Create-flow Cancel bypasses usePrescriptionForm's own confirmation
    // entirely (it's wired straight to prescriptionActions.handlePrescriptionCancel),
    // so a staged-but-unsaved attachment would otherwise be discarded silently.
    const handleNewPrescriptionCancel = useCallback(() => {
        if (rxFormState.pendingAttachmentsCount > 0) {
            const confirmed = window.confirm(
                `You have ${rxFormState.pendingAttachmentsCount} unsaved attachment${rxFormState.pendingAttachmentsCount > 1 ? "s" : ""} that will not be saved. Are you sure you want to cancel?`
            );
            if (!confirmed) return;
        }
        prescriptionActions.handlePrescriptionCancel();
    }, [rxFormState.pendingAttachmentsCount, prescriptionActions.handlePrescriptionCancel]);

    // The context panel sits beside the form only when the form keeps its full width (tables need ~800px)
    const [padWidth, setPadWidth] = useState(0);
    const padObserver = useRef<ResizeObserver | null>(null);
    const padRef = useCallback((el: HTMLDivElement | null) => {
        padObserver.current?.disconnect();
        if (!el) return;
        padObserver.current = new ResizeObserver(([entry]) => setPadWidth(entry.contentRect.width));
        padObserver.current.observe(el);
    }, []);
    // Where the doctor prescribes: wide screens get the mockup's 420px panel; common laptops (1280px, or
    // 1920px at 150% scaling) a 340px one, which still leaves the medicine names ~200px; tablets and
    // narrower keep the whole width for the form, with past visits below it
    const sideBySide = padWidth >= 1100;
    const asideWidth = padWidth >= 1220 ? 420 : 340;

    // ── Effects ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!patientId || !currentUser?.clinic_id || !can('billing:read')) return;
        let cancelled = false;
        getPatientDeposits(patientId, currentUser.clinic_id)
            .then((data) => { if (!cancelled) setOutstanding(Number(data?.outstanding || 0)); })
            .catch(() => { /* the chip is optional; the Billing tab shows its own errors */ });
        return () => { cancelled = true; };
    }, [patientId, currentUser?.clinic_id, activeTab, can]);

    useEffect(() => {
        if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
        getSettings(currentUser.doctor_id, currentUser.clinic_id)
            .then((s) => {
                if (!s) return;
                setDoctor({
                    id:            currentUser.doctor_id  ?? "",
                    name:          s.profile?.name        ?? "",
                    specialty:     s.profile?.specialty   ?? "General Medicine",
                    phone:         s.profile?.phone       ?? "",
                    email:         s.profile?.email       ?? "",
                    clinic_id:     currentUser.clinic_id  ?? "",
                    last_modified: "",
                });
            })
            .catch(() => { /* PrescriptionForm falls back to General Medicine */ });
    }, [currentUser?.doctor_id, currentUser?.clinic_id]);

    // Writing, copying and editing prescriptions: a doctor with prescriptions:write
    const canPrescribe = !!currentUser?.doctor_id && can('prescriptions:write');

    // Auto-open prescription form when URL has ?openPrescription=true
    const { handleCopyPrescription, setShowNewPrescription } = prescriptionActions;
    useEffect(() => {
        if (searchParams.get("openPrescription") !== "true" || !patient || loading || !canPrescribe) return;

        const repeatId = searchParams.get("repeat");
        if (repeatId) {
            const match = visits
                .map((v) => v.prescription)
                .find((p) => p && p.id === repeatId);
            if (match) handleCopyPrescription(match);
            else        setShowNewPrescription(true);
        } else {
            setShowNewPrescription(true);
        }

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("openPrescription");
            if (repeatId) next.delete("repeat");
            return next;
        });
    }, [patient, loading, visits, searchParams, setSearchParams, handleCopyPrescription, setShowNewPrescription, canPrescribe]);


    // Track patient header and tab-row heights: sticky offsets below (phones pin only the tabs) and the pad's side panel.
    // Re-run once the patient has loaded — the header doesn't exist while the page is loading.
    const headerReady = !loading && !!patient;
    useEffect(() => {
        const el = patientHeaderRef.current;
        if (!el) return;
        const update = () => {
            const tabs = el.querySelector('[role="tablist"]');
            document.documentElement.style.setProperty("--patient-header-height", `${el.getBoundingClientRect().height}px`);
            document.documentElement.style.setProperty("--patient-tabs-height", `${tabs ? tabs.getBoundingClientRect().height : 0}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [headerReady]);

    // ── Guards — all hooks above this line ──────────────────────────────────────

    const isInvalidId = !patientId || patientId === "new" || patientId.length < 5;
    if (isInvalidId) {
        return (
            <div className="prescription-form-container">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-foreground mb-2">Invalid Patient ID</h2>
                        <p className="text-muted-foreground mb-4">The patient ID provided is not valid.</p>
                        {onNavigateBack && (
                            <Button onClick={onNavigateBack}>
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Go Back
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="prescription-form-container">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading patient details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!patient) {
        return (
            <Card className="medical-card">
                <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                            <User className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-3">Patient Not Found</h3>
                        <p className="text-muted-foreground mb-6">
                            The patient you're looking for doesn't exist or couldn't be loaded.
                        </p>
                        <Button onClick={onNavigateBack || (() => window.history.back())}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Go Back to Patients
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // ── Handlers ────────────────────────────────────────────────────────────────

    // Back to this patient (not the Prescriptions list) once the edit is saved or cancelled
    const handleEditPrescription = (prescription: { id: string }) =>
        navigate(`/prescriptions/edit/${prescription.id}`, { state: { returnTo: `/patients/${patientId}` } });

    const handleEditPatient = () => setShowEditModal(true);
    const handlePatientUpdated = () => { fetchPatientData(); setShowEditModal(false); };

    // ── Render ──────────────────────────────────────────────────────────────────

    return (
        // No page gutter: the patient header runs edge to edge under the top bar, as in the mockup;
        // the tabs and the pad keep the 20px / 24px content spacing
        <div className="min-h-screen bg-background">
            <Tabs value={activeTab} onValueChange={setActiveTab}>

            {/* ── Unified sticky header (patient info + alerts + tabs) ── */}
            <div
                ref={patientHeaderRef}
                className={cn(
                    "sticky z-[25] bg-card border-b border-border md:top-[var(--topbar-height,60px)]",
                    // Phones: the block sticks so that only its tab row stays in view; banner and vitals scroll away
                    "top-[calc(var(--topbar-height,60px)-var(--patient-header-height,0px)+var(--patient-tabs-height,0px))]",
                )}
            >
                {/* Patient banner — identity and safety chips, same on every patient screen */}
                <PatientBanner
                    patient={patient}
                    size={prescriptionActions.showNewPrescription ? "compact" : "full"}
                    outstanding={outstanding}
                    meta={<>
                        <span>
                            {visitCount} visit{visitCount !== 1 ? "s" : ""}
                            {firstVisit && visitCount > 0 && !prescriptionActions.showNewPrescription && ` since ${format(firstVisit, "MMM yyyy")}`}
                        </span>
                        <span aria-hidden>·</span>
                        <button
                            type="button"
                            onClick={() => setShowDetails(true)}
                            className="touch-target font-medium text-primary hover:underline underline-offset-2"
                        >
                            Patient details
                        </button>
                    </>}
                    leading={!prescriptionActions.showNewPrescription && onNavigateBack ? (
                        <Button variant="outline" size="sm" onClick={onNavigateBack} className="gap-1">
                            <ArrowLeft className="w-4 h-4" /> Back
                        </Button>
                    ) : undefined}
                    actions={<>
                        {!prescriptionActions.showNewPrescription && currentUser?.doctor_id && (
                            <ConsultationControls patientId={safePatientId} />
                        )}
                        {!prescriptionActions.showNewPrescription && can('appointments:write') && (
                            <Button size="sm" variant="outline" onClick={() => setBooking(true)} className="gap-1.5">
                                <CalendarPlus className="w-4 h-4" /> Book
                            </Button>
                        )}
                        {!prescriptionActions.showNewPrescription && can('billing:write') && (
                            <Button size="sm" variant="outline" onClick={() => { setActiveTab("billing"); setNewBill(true); }} className="gap-1.5">
                                <ReceiptIndianRupee className="w-4 h-4" /> New bill
                            </Button>
                        )}
                        {!prescriptionActions.showNewPrescription && canPrescribe && (
                            <Button size="sm" onClick={prescriptionActions.handleNewPrescription} className="gap-1.5">
                                <Plus className="w-4 h-4" /> New prescription
                            </Button>
                        )}
                    </>}
                />

                {/* Today's vitals — in view while the doctor prescribes */}
                <VitalsStrip
                    ageMonths={patientAgeMonths(patient)}
                    prescribing={prescriptionActions.showNewPrescription}
                />

                {/* Tab bar — hidden during prescription mode */}
                {!prescriptionActions.showNewPrescription && (
                    <TabsList className="flex w-full justify-start gap-3 sm:gap-6 overflow-x-auto bg-transparent rounded-none h-auto p-0 px-4 sm:px-6 [scrollbar-width:none]">
                        <TabsTrigger value="overview" className="flex-shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm">
                            <span className="text-sm">Overview</span>
                        </TabsTrigger>
                        <TabsTrigger value="prescriptions" className="flex-shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-xs sm:text-sm">Visits</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger value="vitals" className="flex-shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-xs sm:text-sm">Vitals</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger value="documents" className="flex-shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="hidden sm:inline">Documents</span>
                                <span className="sm:hidden text-xs">Docs</span>
                            </div>
                        </TabsTrigger>
                        {can('billing:read') && (
                            <TabsTrigger value="billing" className="flex-shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="hidden sm:inline">Billing</span>
                                    <span className="sm:hidden text-xs">Bills</span>
                                </div>
                            </TabsTrigger>
                        )}
                        {hasFeature('ipd') && (
                            <TabsTrigger value="ipd" className="flex-shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs sm:text-sm">IPD</span>
                                </div>
                            </TabsTrigger>
                        )}
                    </TabsList>
                )}
            </div>

            {/* ── Tab content ── */}
            {!prescriptionActions.showNewPrescription && (
                <>
                    {/* Overview Tab — last visit, earlier visits, vitals trend */}
                    <TabsContent value="overview" className="px-4 sm:px-6 py-5 mt-0">
                        <PatientOverviewTab
                            patientId={patientId}
                            visits={visits}
                            visitRecords={visitRecords}
                            visitCount={visitCount}
                            ageMonths={patientAgeMonths(patient)}
                            onViewPrescription={prescriptionActions.handleViewPrescription}
                            onCopyPrescription={canPrescribe ? prescriptionActions.handleCopyPrescription : undefined}
                            onEditPrescription={canPrescribe ? handleEditPrescription : undefined}
                            onOpenTab={setActiveTab}
                            appointments={appointments}
                            onAppointmentsChanged={fetchPatientData}
                            currentMedications={patient.current_medications}
                        />
                    </TabsContent>

                    {/* Visits Tab */}
                    <TabsContent value="prescriptions" className="px-4 sm:px-6 py-5 mt-0">
                        <VisitTimeline
                            visits={visits}
                            visitRecords={visitRecords}
                            ageMonths={patientAgeMonths(patient)}
                            onEditPrescription={canPrescribe ? handleEditPrescription : undefined}
                            onViewPrescription={prescriptionActions.handleViewPrescription}
                            onCopyPrescription={canPrescribe ? prescriptionActions.handleCopyPrescription : undefined}
                        />
                    </TabsContent>

                    {/* Vitals Tab — trends and every reading, with corrections */}
                    <TabsContent value="vitals" className="px-4 sm:px-6 py-5 mt-0">
                        {activeTab === "vitals" && (
                            <PatientVitalsTab patientId={patientId} patientName={patient.name} canRecord={can('queue:write')} ageMonths={patientAgeMonths(patient)} />
                        )}
                    </TabsContent>

                    {/* Billing Tab */}
                    {can('billing:read') && (
                        <TabsContent value="billing" className="px-4 sm:px-6 py-5 mt-0">
                            <BillingTab
                                patientId={patientId}
                                patient={{
                                    id:      patient.id,
                                    name:    patient.name,
                                    phone:   patient.phone,
                                    address: patient.address,
                                    age:     patient.age,
                                    gender:  patient.gender,
                                    mrn:     patient.mrn,
                                    date_of_birth: patient.date_of_birth,
                                }}
                                doctorId={currentUser?.doctor_id || ""}
                                clinicId={currentUser?.clinic_id || ""}
                                autoOpenCreate={newBill}
                                onAccountChange={setOutstanding}
                                onAutoOpened={clearNewBill}
                            />
                        </TabsContent>
                    )}

                    {/* IPD Tab */}
                    {hasFeature('ipd') && (
                        <TabsContent value="ipd" className="px-4 sm:px-6 py-5 mt-0">
                            <PatientIPDTab
                                patientId={patientId}
                                patient={{
                                    name:   patient.name,
                                    age:    patient.age,
                                    gender: patient.gender,
                                    phone:  patient.phone,
                                    mrn:    patient.mrn,
                                }}
                                clinicId={currentUser?.clinic_id || ""}
                                canWrite={can('ipd:write')}
                            />
                        </TabsContent>
                    )}

                    {/* Documents Tab — patient-level, not tied to a specific visit */}
                    <TabsContent value="documents" className="px-4 sm:px-6 py-5 mt-0">
                        <PatientDocuments patientId={patientId} link={{}} />
                    </TabsContent>
                </>
            )}

            {/* ── Prescription Creation ── */}
            {prescriptionActions.showNewPrescription && (
                <div ref={padRef} className="px-4 sm:px-6">
                    {/* Form — no internal sticky bar */}
                    <PrescriptionForm
                        key={prescriptionActions.copyNonce}
                        mode="create"
                        patientId={patientId}
                        initialData={prescriptionActions.copyData || undefined}
                        onSaved={(printRx) => {
                            prescriptionActions.handlePrescriptionSaved();
                            setActiveTab("overview");
                            const then = afterSaveRef.current;
                            afterSaveRef.current = null;
                            if (then) { then(); return; }
                            // "Save & print": straight into the print preview of what was just saved
                            if (printRx) prescriptionActions.handleViewPrescription(printRx);
                        }}
                        onCancel={handleNewPrescriptionCancel}
                        showStickyBar={false}
                        onFormReady={handleFormReady}
                        patient={patient}
                        doctor={doctor ?? undefined}
                        lastPrescription={visits.find(v => v.prescription?.medicines?.some(m => m.product?.trim()))?.prescription}
                        aside={sideBySide ? <ContextPanel visits={visits} onViewPrescription={prescriptionActions.handleViewPrescription} /> : undefined}
                        asideWidth={asideWidth}
                    />

                    {/* Action bar — sticky bottom, tied to form.
                        Sticks while form fields are in view; scrolls off naturally
                        once the user passes into Previous Visits. */}
                    <FormActionBar
                        // Bleed out of the pad's px-4/sm:px-6 so the bar spans the same width as the top bars
                        className="-mx-4 sm:-mx-6 [&>div]:px-4 sm:[&>div]:px-6"
                        hasChanges={rxFormState.hasChanges}
                        hasValidChanges={rxFormState.hasValidChanges}
                        isSubmitting={rxFormState.isSubmitting}
                        mode="create"
                        onSubmit={() => { afterSaveRef.current = null; rxSubmitRef.current?.(); }}
                        // Preview hidden for now: onPreview={() => rxActionsRef.current?.preview()}
                        onSubmitAndPrint={() => { afterSaveRef.current = null; rxActionsRef.current?.submitAndPrint(); }}
                        // Through the form: asks before dropping changes and clears the saved draft
                        onCancel={() => { afterSaveRef.current = null; rxActionsRef.current?.cancel(); }}
                        attachmentsSlot={rxFormState.attachmentsSlot}
                        summary={rxFormState.summary}
                        queueSlot={currentUser?.doctor_id
                            ? <ConsultationControls patientId={safePatientId} hasUnsavedChanges={rxFormState.hasChanges}
                                                    onSaveFirst={saveThen} onMainShown={setQueueMain} />
                            : undefined}
                        queueMain={!!currentUser?.doctor_id && queueMain}
                    />

                    {/* Previous Visits — below the form when there's no room for the ContextPanel beside it */}
                    {!sideBySide && visits.length > 0 && (
                        <div className="bg-background border-t border-border/20">
                            <div className="px-3 py-2.5 border-b border-border/10 bg-primary/5 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-primary" />
                                    <h2 className="font-medium text-foreground text-sm">Previous Visits</h2>
                                </div>
                            </div>
                            <div className="p-3">
                                <VisitTimeline
                                    visits={visits}
                                    visitRecords={visitRecords}
                                    ageMonths={patientAgeMonths(patient)}
                                    onEditPrescription={canPrescribe ? handleEditPrescription : undefined}
                                    onViewPrescription={prescriptionActions.handleViewPrescription}
                                    onCopyPrescription={canPrescribe ? prescriptionActions.handleCopyPrescription : undefined}
                                    prescribing
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            </Tabs>

            {/* ── Modals ── */}
            <Sheet open={showDetails} onOpenChange={setShowDetails}>
                <SheetContent side="right" className="w-full sm:max-w-[480px] p-0 gap-0 flex flex-col">
                    <SheetHeader className="px-5 py-3.5 border-b border-border text-left space-y-0.5">
                        <SheetTitle className="text-base capitalize">{patient.name}</SheetTitle>
                        <SheetDescription className="text-xs">
                            Patient details{patient.mrn && <> · MRN <span className="font-mono">{patient.mrn}</span></>}
                        </SheetDescription>
                    </SheetHeader>
                    <PatientDetailsPanel
                        patient={patient}
                        visitCount={visitCount}
                        lastVisit={lastVisit}
                        onEdit={can('patients:write') ? () => { setShowDetails(false); handleEditPatient(); } : undefined}
                    />
                </SheetContent>
            </Sheet>

            {booking && (
                <AddAppointmentModal
                    isOpen={booking}
                    onClose={() => setBooking(false)}
                    patientId={patient.id}
                    patients={[patient] as any}
                    initialDate={followUpDate}
                    onAdded={() => {
                        setBooking(false);
                        fetchPatientData();
                    }}
                />
            )}
            {prescriptionActions.viewingPrescription && (
                <ViewPrescriptionModal
                    open={true}
                    prescription={prescriptionActions.viewingPrescription}
                    onClose={prescriptionActions.handleCloseView}
                />
            )}

            {patient && (
                <PatientFormModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={handlePatientUpdated}
                    mode="edit"
                    patientId={patientId}
                    initialData={{
                        name:                    patient.name                    || "",
                        age:                     patient.age?.toString()         || "",
                        date_of_birth:           patient.date_of_birth           || "",
                        gender:                  patient.gender                  || "Male",
                        phone:                   patient.phone                   || "",
                        phone_2:                 patient.phone_2                 || "",
                        email:                   patient.email                   || "",
                        address:                 patient.address                 || "",
                        city:                    patient.city                    || "",
                        pin_code:                patient.pin_code                || "",
                        preferred_language:      patient.preferred_language      || "English",
                        aadhar_number:           patient.aadhar_number           || "",
                        blood_group:             patient.blood_group             || "",
                        allergies:               patient.allergies               || "",
                        chronic_conditions:      patient.chronic_conditions      || "",
                        current_medications:     patient.current_medications     || "",
                        emergency_contact_name:  patient.emergency_contact_name  || "",
                        emergency_contact_phone: patient.emergency_contact_phone || "",
                        payment_preference:      patient.payment_preference      || "Cash",
                    }}
                    showPrescriptionOption={false}
                />
            )}
        </div>
    );
}
